import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Lightbox from '../components/Lightbox';
import SEO from '../components/SEO';

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallerySlug, setSelectedGallerySlug] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeImagesList, setActiveImagesList] = useState([]);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/gallery')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setGalleries(data);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching galleries:', err);
        setLoading(false);
      });
  }, []);

  // Get all images to display based on selected filter
  const getDisplayImages = () => {
    if (selectedGallerySlug === 'all') {
      // Flatten all images from all galleries
      const allImages = [];
      galleries.forEach(g => {
        if (Array.isArray(g.images)) {
          g.images.forEach(img => {
            if (!allImages.includes(img)) {
              allImages.push(img);
            }
          });
        }
      });
      return allImages;
    }

    const found = galleries.find(g => g.gallery_slug === selectedGallerySlug || (g.slug && g.slug.da === selectedGallerySlug));
    return found && Array.isArray(found.images) ? found.images : [];
  };

  const handleImageClick = (imgUrl, imagesList) => {
    const idx = imagesList.indexOf(imgUrl);
    setActiveImagesList(imagesList);
    setLightboxIndex(idx !== -1 ? idx : 0);
  };

  const displayImages = getDisplayImages();

  return (
    <div className="bg-brand-bg">
      <SEO
        title="Galleri – Projekter, Porte & Sikring"
        description="Se vores billedgalleri med udført portservice, reparation, montage af ledhejseporte, hurtigporte og Ajax alarmsystemer i Vejen og hele Syddanmark."
        url="/galleri"
        keywords="galleri porte, portservice billeder, montage garageporte, industriporte Vejen, Pabst Teknik projekter"
      />
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-slate-950 via-brand-dark to-brand-accent/20 text-white px-6 py-24 md:py-32 overflow-hidden">
        {/* Background Image with Blend Mode */}
        <div className="absolute inset-0 opacity-15 mix-blend-overlay">
          <img
            src="https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-img20240717114443-69-11zon.webp"
            alt="Pabst Teknik galleri over portmontage og tekniske installationer"
            className="h-full w-full object-cover"
          />
        </div>
        
        {/* Subtle Tech Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(2,132,199,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,132,199,0.08)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
        
        {/* Glowing Ambient Light */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-accent/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="mx-auto max-w-7xl relative z-10 flex flex-col gap-6 items-center text-center">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Brødkrummesti" className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-300">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1.5">
              <i className="fa-solid fa-house text-xs text-brand-accent" />
              <span>Forside</span>
            </Link>
            <i className="fa-solid fa-chevron-right text-[10px] text-slate-400" />
            <span className="text-brand-accent font-semibold" aria-current="page">Galleri</span>
          </nav>

          {/* Floating Badge */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-brand-accent/10 text-brand-accent border border-brand-accent/20 uppercase tracking-widest">
            <i className="fa-solid fa-images text-[10px] animate-pulse" /> Billeder af vores arbejde
          </span>

          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-6xl font-display">
            Vores <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-brand-accent">Galleri</span>
          </h1>
          
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed font-sans">
            Se eksempler på vores professionelle arbejde hos <Link to="/" className="text-white hover:text-brand-accent underline decoration-brand-accent/50 underline-offset-2 transition-colors font-medium">Pabst Teknik</Link> med portservice, montage af porte, alarmsystemer og videoovervågning.
          </p>
        </div>
      </section>

      {/* Gallery Grid & Filter */}
      <section className="relative z-20 px-6 pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl -mt-12 bg-white rounded-3xl border border-slate-100 p-8 md:p-16 shadow-xl">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-accent" />
              <p className="text-sm font-semibold text-slate-500">Henter galleribilleder...</p>
            </div>
          ) : (
            <div className="flex flex-col gap-12">
              {/* Category Filter Tabs */}
              <div className="flex flex-wrap items-center justify-center gap-3 border-b border-slate-100 pb-8">
                <button
                  onClick={() => setSelectedGallerySlug('all')}
                  className={`rounded-full px-6 py-2.5 text-sm font-bold transition-all cursor-pointer ${
                    selectedGallerySlug === 'all'
                      ? 'bg-brand-accent text-white shadow-md'
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Vis alle billeder
                </button>
                {galleries.map((g) => {
                  const slugVal = g.gallery_slug || (g.slug && g.slug.da);
                  return (
                    <button
                      key={g.id}
                      onClick={() => setSelectedGallerySlug(slugVal)}
                      className={`rounded-full px-6 py-2.5 text-sm font-bold transition-all cursor-pointer ${
                        selectedGallerySlug === slugVal
                          ? 'bg-brand-accent text-white shadow-md'
                          : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {g.title.da}
                    </button>
                  );
                })}
              </div>

              {/* Gallery Description */}
              {selectedGallerySlug !== 'all' && (
                <div className="text-center max-w-2xl mx-auto -mt-4">
                  <p className="text-sm text-slate-500 leading-relaxed italic">
                    {galleries.find(g => (g.gallery_slug || (g.slug && g.slug.da)) === selectedGallerySlug)?.description.da}
                  </p>
                </div>
              )}

              {/* Images Grid */}
              {displayImages.length === 0 ? (
                <div className="text-center py-20">
                  <i className="fa-regular fa-images text-4xl text-slate-300 mb-4 block" />
                  <p className="text-sm font-semibold text-slate-500">Der er i øjeblikket ingen billeder i dette galleri.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {displayImages.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleImageClick(img, displayImages)}
                      className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-50 border border-slate-100 cursor-pointer shadow-sm transition-all hover:shadow-md"
                    >
                      <img
                        src={img}
                        alt={`Pabst Teknik referencebillede ${idx + 1} - Udført professionelt portarbejde og teknisk installation`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-slate-950/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-brand-dark shadow-lg">
                          <i className="fa-regular fa-magnifying-glass-plus text-lg" />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Cross-linking & CTA inside Gallery */}
              <div className="mt-8 pt-10 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/ydelser"
                  className="p-6 rounded-2xl bg-slate-50 hover:bg-white border border-slate-100 hover:border-brand-accent/30 hover:shadow-md transition-all flex flex-col justify-between group"
                >
                  <div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent mb-4 group-hover:bg-brand-accent group-hover:text-white transition-colors">
                      <i className="fa-solid fa-garage text-sm" />
                    </span>
                    <h4 className="font-display text-base font-bold text-brand-dark group-hover:text-brand-accent transition-colors">
                      Vores Ydelser
                    </h4>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                      Læs mere om de forskellige typer porte, automatik, lovpligtigt eftersyn og Ajax sikringssystemer.
                    </p>
                  </div>
                  <span className="text-xs font-bold text-brand-accent inline-flex items-center gap-1.5 mt-4 group-hover:gap-2 transition-all">
                    <span>Udforsk ydelser</span>
                    <i className="fa-regular fa-arrow-right text-[10px]" />
                  </span>
                </Link>

                <Link
                  to="/om-os"
                  className="p-6 rounded-2xl bg-slate-50 hover:bg-white border border-slate-100 hover:border-brand-accent/30 hover:shadow-md transition-all flex flex-col justify-between group"
                >
                  <div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent mb-4 group-hover:bg-brand-accent group-hover:text-white transition-colors">
                      <i className="fa-solid fa-building-user text-sm" />
                    </span>
                    <h4 className="font-display text-base font-bold text-brand-dark group-hover:text-brand-accent transition-colors">
                      Om Pabst Teknik
                    </h4>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                      Lær os bedre at kende, læs om vores værdier, erfarne montører og salgspartnerskab med Lindab.
                    </p>
                  </div>
                  <span className="text-xs font-bold text-brand-accent inline-flex items-center gap-1.5 mt-4 group-hover:gap-2 transition-all">
                    <span>Læs om os</span>
                    <i className="fa-regular fa-arrow-right text-[10px]" />
                  </span>
                </Link>

                <Link
                  to="/kontakt"
                  className="p-6 rounded-2xl bg-brand-dark text-white hover:bg-slate-900 border border-slate-800 shadow-md transition-all flex flex-col justify-between group"
                >
                  <div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-accent text-white mb-4">
                      <i className="fa-solid fa-envelope text-sm" />
                    </span>
                    <h4 className="font-display text-base font-bold text-white">
                      Få et tilbud
                    </h4>
                    <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                      Fandt du inspiration i galleriet? Send os dine mål eller ønsker, og modtag et uforpligtende tilbud.
                    </p>
                  </div>
                  <span className="text-xs font-bold text-brand-accent inline-flex items-center gap-1.5 mt-4 group-hover:gap-2 transition-all">
                    <span>Gå til kontakt</span>
                    <i className="fa-regular fa-arrow-right text-[10px]" />
                  </span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && activeImagesList.length > 0 && (
        <Lightbox
          images={activeImagesList}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((lightboxIndex - 1 + activeImagesList.length) % activeImagesList.length)}
          onNext={() => setLightboxIndex((lightboxIndex + 1) % activeImagesList.length)}
        />
      )}
    </div>
  );
}
